/**
 * A wall that RETURNS into the room is drawn end-on.
 *
 * Where an interior wall meets the wall being drawn and runs toward the
 * viewer, an elevation shows it as a tall band the width of that wall's
 * thickness — you are looking at its end. Without it the drawing reads as
 * one unbroken face and says nothing about the partition standing there.
 *
 * The band is read off the ROOM's own boundary, not off structure walls:
 * the embedded elevations host is handed a slim structure with no walls at
 * all, so a structure-wall lookup would silently draw nothing there.
 *
 * The rule is the room's floor polygon leaving the wall's line and coming
 * back to it — an EXCURSION. That is what a return wall is, and it needs no
 * size threshold to recognise: the excursion's own width IS the thickness,
 * whether it is a 4" stud wall or a chimney breast. Two excursions are not
 * returns: one that runs OUTSIDE the room is a recess, and the one that
 * CONTAINS the wall's whole span is the rest of the room — every polygon
 * eventually comes back to the wall's line at its far end.
 *
 * A return does NOT have to sit inside the wall's span. Where a partition
 * meets a long wall the scan splits that wall in two, and the return lands
 * in the gap BETWEEN the two collinear segments, touching each of them end
 * on (Girdwood's kitchen, walls 87205860 | 6C4977B3 | AD92694A). So a band
 * is claimed by any wall on its line whose span it touches, and the caller
 * de-duplicates the one both segments of a grouped elevation claim.
 *
 * The last rule is what makes it an END: the excursion must run FURTHER
 * into the room than it is wide. That is the whole difference between a
 * wall seen end-on and the room's own shape — a jog, a bulkhead or a
 * chimney breast is wider than it is deep, and what you see of it is its
 * FACE, which the drawing already has a wall for. No size threshold, only
 * the two numbers compared against each other.
 *
 * A band is emitted only when ROOM WALLS run along the WHOLE excursion:
 * they are what gives it a height, and a boundary detour that no wall
 * describes is a floor plate stepping around something we have no evidence
 * is a wall at all (D7155424's loft, whose four walls describe none of it).
 */

/** On the wall's line, and coincident corners, in metres. */
const TOL = 0.06;

const corner = (c) => ({ x: c?.x ?? 0, z: c?.z ?? 0 });

/** Plan basis of a rectangular wall: `a` along the drawing's X, `n` its normal. */
function wallBasis(wall) {
  const r = wall?.rotation;
  const ry = Array.isArray(r) ? r[1] || 0 : r?.y || 0;
  return {
    ax: Math.cos(ry),
    az: -Math.sin(ry),
    nx: Math.sin(ry),
    nz: Math.cos(ry),
    cx: wall?.center?.x ?? 0,
    cz: wall?.center?.z ?? 0,
    half: (wall?.dimensions?.x || 0) / 2,
  };
}

/** Every wall of the room as a plan segment, for the height lookup. */
function wallSegments(room) {
  return (room?.walls || [])
    .map((w) => {
      const b = wallBasis(w);
      if (!b.half) return null;
      return {
        height: w?.dimensions?.y || 0,
        p0: { x: b.cx - b.ax * b.half, z: b.cz - b.az * b.half },
        p1: { x: b.cx + b.ax * b.half, z: b.cz + b.az * b.half },
      };
    })
    .filter(Boolean);
}

const near = (a, b) => Math.hypot(a.x - b.x, a.z - b.z) <= TOL;

/** Tallest room wall running between these two corners, either way round. */
function wallHeightBetween(segments, a, b) {
  let best = 0;
  for (const s of segments) {
    if ((near(s.p0, a) && near(s.p1, b)) || (near(s.p0, b) && near(s.p1, a))) {
      best = Math.max(best, s.height);
    }
  }
  return best;
}

/**
 * The end-on wall bands to draw on one wall's elevation.
 *
 * @param {Object} wall - The wall being drawn (a rectangular room wall).
 * @param {Object} room - Its room, for `floors[0].polygonCorners` + `walls`.
 * @returns {Array<{a: {x,z}, b: {x,z}, height: number, depth: number}>}
 *   `a`/`b` are the band's two edges as WORLD plan points on the wall's
 *   line — the caller projects them with the same basis it draws objects
 *   with, which for a GROUPED wall is the group's, not this wall's own.
 *   `height` is metres above the floor; `depth` is how far the return runs
 *   into the room.
 */
export function endOnWallBands(wall, room) {
  const polygon = (room?.floors?.[0]?.polygonCorners || []).map(corner);
  const b = wallBasis(wall);
  if (polygon.length < 3 || !b.half) return [];

  // Which side of the wall the room is on — the polygon's own mean point.
  const mid = polygon.reduce(
    (acc, p) => ({
      x: acc.x + p.x / polygon.length,
      z: acc.z + p.z / polygon.length,
    }),
    { x: 0, z: 0 }
  );
  const inward = Math.sign((mid.x - b.cx) * b.nx + (mid.z - b.cz) * b.nz) || 1;

  const pts = polygon.map((p) => ({
    ...p,
    s: (p.x - b.cx) * b.ax + (p.z - b.cz) * b.az,
    d: ((p.x - b.cx) * b.nx + (p.z - b.cz) * b.nz) * inward,
  }));
  const n = pts.length;
  // On the wall's LINE, not inside its span — a return at the end of a wall
  // is bounded by a corner just past it.
  const onLine = pts.map((p) => Math.abs(p.d) <= TOL);

  const segments = wallSegments(room);
  const bands = [];
  for (let i = 0; i < n; i++) {
    if (!onLine[i]) continue;
    // Walk forward over the corners that leave the line.
    let j = (i + 1) % n;
    let steps = 0;
    let depth = 0;
    while (!onLine[j] && steps < n) {
      depth = Math.max(depth, pts[j].d);
      j = (j + 1) % n;
      steps += 1;
    }
    if (steps === 0 || steps >= n) continue;
    if (depth <= TOL) continue; // a return runs INTO the room, not out of it

    const start = Math.min(pts[i].s, pts[j].s);
    const end = Math.max(pts[i].s, pts[j].s);
    if (end - start < TOL / 2) continue;
    // The excursion that swallows the wall is the rest of the room.
    if (start <= -b.half + TOL && end >= b.half - TOL) continue;
    // …and one that never reaches this wall belongs to another one.
    if (end < -b.half - TOL || start > b.half + TOL) continue;

    // Its height is the height of the walls standing on it — and every edge
    // of the excursion must BE one, or this is not a wall we can see.
    let height = 0;
    let k = i;
    let described = true;
    for (let step = 0; step <= steps; step += 1) {
      const next = (k + 1) % n;
      const edge = wallHeightBetween(segments, pts[k], pts[next]);
      if (edge <= 0) described = false;
      height = Math.max(height, edge);
      k = next;
    }
    if (!described || height <= 0) continue;
    // Seen END-on: it runs away from this wall further than it spans it.
    if (depth <= end - start) continue;

    // Back to world, on the wall's line: the caller owns the projection.
    const point = (t) => ({
      x: b.cx + b.ax * t,
      z: b.cz + b.az * t,
    });
    bands.push({ a: point(start), b: point(end), height, depth });
  }
  return bands;
}
